import api from "@/api/spaceMountainAPI";
import adminApi from "@/api/spaceMountainAdminAPI";
import { defineStore } from "pinia";
import User from "@/types/User";
import Preferences from "@/types/Preferences";
import { AxiosResponse } from "axios";
import { setAuthorization, clearAuthorization } from '@/http/index';
import { useFoldersStore } from "@/stores/folders";
import {LocationQueryValue} from "vue-router";

export const useUserStore = defineStore({
    id: "userStore",
    state: () => ({
        user: {
            companyName: '',
            role: '',
            email: '',
            firstname: '',
            id: '',
            lastname: '',
            preferences: {},
            responsableId: ''
        } as User,
        isLogged: false as boolean,
        userLoader: false,
        randomColor: '#d04139',
        notifications: [],
    }),

    actions: {

        goLoader() {
            this.userLoader = true;
        },

        stopLoader() {
            this.userLoader = false;
        },

        redirectApi() {
            if (this.user.role === 'admin' || this.user.role === 'superAdmin') {
                return adminApi;
            } else return api;
        },

        login(email: string, password: string) {
            return api.login(email, password).then((response: AxiosResponse) => {
                if (response.status === 200) {
                    this.fillInfos(response.data.user, response.data.user.preferences, response.data.token.s);
                }
                return response;
            });
        },

        fillInfos(user: User, preferences: string, token: string | null | LocationQueryValue[]) {
            localStorage.setItem('sqg-auth', JSON.stringify({ token: token }));
            setAuthorization(token);
            this.isLogged = true;
            this.user = {
                companyName: user.companyName,
                role: user.role,
                email: user.email,
                firstname: user.firstname,
                id: user.id,
                lastname: user.lastname,
                preferences: this.loadPreferences(preferences),
                notifications: [],
                responsableId: ''
            };
            if (this.user.preferences.currentFolderId) {
                const foldersStore = useFoldersStore();
                foldersStore.getFolderById(this.user.preferences.currentFolderId).then((response: AxiosResponse) => {
                    if (response.status === 200) {
                        foldersStore.setCurrentFolder(response.data);
                    }
                });
            }
        },

        logout() {
            this.isLogged = false;
            clearAuthorization();
            localStorage.removeItem('sqg-auth');
        },

        signup(user: Object) {
            return api.signup(user).then((response) => {
                return response;
            });
        },

        check() {
            return api.check().then((response: AxiosResponse) => {
                this.getNotifications();
                this.randomColor = response.data;
                return response;
            });
        },

        loadUserInfo() {
            console.log('tets');
            return api.getUserByToken().then((response) => {
                this.userLoader = true;
                if (response.status === 200) {
                    this.user = {
                        companyName: response.data.companyName,
                        role: response.data.role,
                        email: response.data.email,
                        firstname: response.data.firstname,
                        id: response.data.id,
                        lastname: response.data.lastname,
                        preferences: this.loadPreferences(response.data.preferences),
                        notifications: [],
                        responsableId: ''
                    };
                    this.isLogged = true;
                    if (this.user.preferences.currentFolderId) {
                        const foldersStore = useFoldersStore();
                        foldersStore.getFolderById(this.user.preferences.currentFolderId).then((response: AxiosResponse) => {
                            if (response.status === 200) {
                                foldersStore.setCurrentFolder(response.data);
                            }
                        });
                    }
                    this.userLoader = false;
                }
                return response;
            });
        },

        getUserById(userId: string) {
            this.goLoader();
            return this.redirectApi().getUserById(userId).then((response: AxiosResponse) => {
                this.stopLoader();
                return response;
            });
        },

        setCurrentFolderId(folderId: string) {
            let pref = this.user.preferences;
            pref.currentFolderId = folderId;
            return this.savePreferences(pref).then((response: AxiosResponse) => {
                return response;
            });
        },

        addFolderToFavorite(folderId: string, folderName: string, nbForm: number, nbFile: number, state: string) {
            let pref = this.user.preferences;
            if (!pref.favoriteFolders) pref.favoriteFolders = [{ id: folderId, name: folderName, nbForm: nbForm, nbFile: nbFile, state: state }];
            else {
                let find = pref.favoriteFolders.find((folder) => folder.id === folderId);
                if (!find) pref.favoriteFolders.unshift({ id: folderId, name: folderName, nbForm: nbForm, nbFile: nbFile, state: state });
            }
            this.savePreferences(pref);
        },

        loadPreferences(preferences: string) {
            let pref = JSON.parse(preferences);
            if (pref.currentFolderId === undefined) pref.currentFolderId = '';
            if (pref.notifications === undefined) pref.notifications = [];
            if (pref.oldFolders === undefined) pref.oldFolders = [];
            if (pref.favoriteFolders === undefined) pref.favoriteFolders = [];
            return pref;
        },

        savePreferences(preferences: Preferences) {
            return api.setPreferences(JSON.stringify(preferences)).then((response: AxiosResponse) => {
                return response;
            });
        },

        updatePreferences(folderId: string) {
            const foldersStore = useFoldersStore();
            foldersStore.getFolderById(folderId).then((response: AxiosResponse) => {
                const index = this.user.preferences.favoriteFolders.findIndex((el) => el.id === response.data.id);
                this.user.preferences.favoriteFolders.splice(index, 1, { id: response.data.id, name: response.data.name, nbForm: response.data.forms.length, nbFile: response.data.files.length, state: response.data.state });
                this.savePreferences(this.user.preferences);
            })
        },

        resetPreferences() {
            return api.resetPreferences().then((response: AxiosResponse) => {
                return response;
            });
        },

        getUsersByKey(key: string) {
            return adminApi.getUsersByKey(key).then((response: AxiosResponse) => {
                return response;
            });
        },

        getAllUsers() {
            this.userLoader = true;
            return this.redirectApi().getAllUsers().then((response: AxiosResponse) => {
                this.userLoader = false;
                return response;
            });
        },

        getUsersByFolder(folderId: string) {
            this.userLoader = true;
            return api.getUsersByFolder(folderId).then((response: AxiosResponse) => {
                this.userLoader = false;
                return response;
            });
        },

        setFoldersByUser(userId: string, affectedFolders: Array<string>) {
            return api.setFoldersByUser(userId, affectedFolders).then((response: AxiosResponse) => {
                return response;
            });
        },

        createUser(firstname: string, lastname: string, email: string, password: string, responsableId: string | undefined) {
            this.userLoader = true;
            return api.createUser(firstname, lastname, email, password, responsableId).then((response: AxiosResponse) => {
                this.userLoader = false;
                return response;
            });
        },

        createAdmin(firstname: string, lastname: string, email: string, password: string) {
            this.userLoader = true;
            return adminApi.createAdmin(firstname, lastname, email, password).then((response: AxiosResponse) => {
                this.userLoader = false;
                return response;
            });
        },

        deleteAdmin(userId: string) {
            this.userLoader = true;
            return adminApi.deleteUser(userId).then((response: AxiosResponse) => {
                this.userLoader = false;
                return response;
            });
        },

        updateAdmin(firstname: string, lastname: string) {
            this.userLoader = true;
            return adminApi.updateAdmin(firstname, lastname).then((response: AxiosResponse) => {
                this.userLoader = false;
                return response;
            });
        },

        deleteNotification(notificationId: string) {
            this.userLoader = true;
            return api.deleteNotification(notificationId).then((response: AxiosResponse) => {
                this.userLoader = false;
                return response;
            });
        },

        getNotifications() {
            return api.getNotifications().then((response: AxiosResponse) => {
                this.notifications = response.data;
                return response.data;
            });
        },

        getResponsables() {
            return adminApi.getResponsables().then((response: AxiosResponse) => {
                return response.data;
            });
        },

        changeLogin(email: string, password: string, userId: string) {
            return adminApi.changeLogin(userId, email, password).then((response: AxiosResponse) => {
                return response.data;
            });
        }
    },
});
